import logger, { select, text } from '@clamjs/logger';
import semver from 'semver';

import { getGitRepoInfo } from '../codebase';
import { getCurrentBranch, getLatestGitTags, getOriginMainBranch } from '../gitlab';
import { getO2AppIterationVersions } from './application';
import { getO2Api, getO2BucApi } from './base';
import type { O2IterationSchema } from './types';

/** 根据迭代 id 获取迭代基本信息 */
export async function getO2IterationById(id: number) {
  return await getO2Api<O2IterationSchema>(`/v1.0/work/iterations/${id}/detail`);
}

/** 根据仓库和分支获取 O2 迭代信息，https://icms.alibaba-inc.com/portal/def-work/docs/t2190764.html */
export async function getO2Iteration(params?: { repo?: string; branch?: string; print?: boolean }) {
  const currentBranch = params?.branch || getCurrentBranch();
  const repo = params?.repo || getGitRepoInfo().repo;
  const result = await getO2Api<O2IterationSchema>('/v1.0/work/apps/iterations/infos', {
    query: {
      repo: repo || getGitRepoInfo().repo,
      branch: currentBranch,
    },
  });
  if (params?.print) {
    logger.note(JSON.stringify(result, null, 2), '应用迭代详情数据');
  }
  return result;
}

/**
 * 获取应用对应分支的迭代信息(2 running 、3 suucess、 4 error 、5 废弃)
 * https://icms.alibaba-inc.com/portal/def-work/docs/t2190707.html
 *
 * 与 getO2Iteration 结果相同，这个还需要 buc，故都走上面那个，这个先不删、备用
 */
/*
export async function getO2IterationInfo(params?: {
  repo?: string;
  branch?: string;
  print?: boolean;
}) {
  const repo = params?.repo || getGitRepoInfo().repo;
  const currentBranch = params?.branch || getCurrentBranch();
  const result = await getO2BucApi<O2IterationSchema>(
    `/v1.0/work/apps/${encodeURIComponent(encodeURIComponent(repo))}/branches/iterations`,
    {
      query: {
        branch: encodeURIComponent(currentBranch),
      },
    },
  );
  if (params?.print) {
    logger.info('应用迭代详情数据：', result);
  }
  return result;
}
*/

type SemverType = 'patch' | 'minor' | 'major';

const VERSION_START = '1.0.0';

/** 根据已有最大版本，按照 semver 类型，计算新迭代可用的版本 */
function getNewIterVersion(options: {
  startVersion: string;
  semverType: SemverType;
  existsVersions: string[];
  // 起始版本是否从 startVersion 开始，默认 false，即第一个版本是否包含 startVersion
  include?: boolean;
}) {
  const {
    startVersion = VERSION_START,
    semverType = 'patch',
    existsVersions = [],
    include = false,
  } = options;
  const validMaxVersion = semver.valid(startVersion);
  if (validMaxVersion) {
    let newVersion = include ? startVersion : semver.inc(validMaxVersion, semverType);
    if (newVersion) {
      // 不能跟已存在的迭代版本号相同
      while (existsVersions.includes(newVersion)) {
        newVersion = semver.inc(newVersion, semverType) as string;
      }
      return newVersion;
    }
  }
  throw new Error(
    `计算版本失败，请检查参数 maxVersion: ${startVersion}, semverType: ${semverType}`,
  );
}

/** 获取迭代版本号 */
async function getIterationVersion() {
  /*
   * 一些规则：
   * 版本号应该基于已发布的最大版本来递增 x、y 或 z
   * 版本号不能和已有的 O2 迭代的版本号相同，包含未发布状态的迭代
   * 已经发布的版本有对应的 git tag，格式为 publish/{x.y.z}
   * 更多：https://aliyuque.antfin.com/fliggyfe-infrateam/qvrf4a/bx9tluhyy7zmuv3s#HQPuG
   *
   * 计算迭代版本
   * 方法一：通过获取 O2 应用的所有迭代版本号，基于已发布的 O2 迭代的最大版本号递增。目前 O2 接口返回只有版本号，没有状态，所以此路不通。
   * 方法二：通过获取 O2 应用的迭代列表可以实现上条，不过列表数据较大，目前是分页查询的，也不好实现。
   * 方法三：通过找出 git tag 最大版本。目前采用此方法实现。
   */
  const o2IterationVersions = await getO2AppIterationVersions();
  const tagPrefix = 'publish/';
  const latestPublishedTags = getLatestGitTags(tagPrefix, true);
  let newVersion = VERSION_START;
  if (latestPublishedTags.length) {
    const top20Tags = latestPublishedTags.slice(0, 20);
    top20Tags.sort((a, b) => {
      const aVersion = a.replace(tagPrefix, '');
      const bVersion = b.replace(tagPrefix, '');
      return semver.compare(aVersion, bVersion);
    });
    const maxVersion = top20Tags[0]!.replace(tagPrefix, '');
    const presetSemverTypes: SemverType[] = ['patch', 'minor', 'major'];
    const presetChoices = presetSemverTypes.map((name) => {
      const version = getNewIterVersion({
        startVersion: maxVersion,
        semverType: name,
        existsVersions: o2IterationVersions,
      });
      return {
        label: name,
        value: version,
        hint: `${name} 升级: ${version}`,
      };
    });
    const askedVersion = await select({
      message: `请选择本次迭代的版本号（已发布的最大版本为 ${maxVersion})`,
      options: [
        ...presetChoices,
        {
          label: 'input',
          value: 'input',
          hint: '自定义版本号',
        },
      ],
      initialValue: 'patch',
    });
    if (askedVersion === 'input') {
      newVersion = await text({
        message: '请输入新版本号（格式 x.y.z)',
        validate: (value) => {
          const valid = /^\d+\.\d+\.\d+$/u.test(value);
          if (!valid) {
            return '请输入正确的版本号';
          }
          const isIterationVersionExists = o2IterationVersions.some((version) => version === value);
          if (isIterationVersionExists) {
            return '已存在此版本号的 O2 迭代，请重新输入';
          }
          return undefined;
        },
      });
    } else {
      newVersion = askedVersion;
    }
  } else {
    // 计算和已有不重复的新版本
    newVersion = getNewIterVersion({
      startVersion: newVersion,
      semverType: 'patch',
      existsVersions: o2IterationVersions,
      include: true,
    });
  }

  return newVersion;
}

interface CreateO2IterationOptions {
  repo?: string;
  name?: string;
  version?: string;
  /** 迭代对应的主干分支，可不传。默认为 master。 */
  trunk?: string;
  description?: string;
  /** 添加变更的方式，新建变更 'new', 已有变更 ’exist', 不添加变更：不传 */
  branchType?: 'exist' | 'new';
  /**
   * 添加变更是的分支，如果 branchType = 'new' 必传，如果 branchType = 'exist' 则可不传默认走当前分支
   */
  branch?: string;
  /**
   * 新建变更要绑定的 aone 工作项 格式如下：(req|task|issue)|AONE_ID；前者表示是 aone 的需求或任务或缺陷，后者表示工作项 id； 例如
   * req|1101，issue|0020，多个的情况使用英文逗号分隔，如 “req|1101,issue|0020”
   */
  aoneBinds?: string;
}

/** 创建迭代，https://icms.alibaba-inc.com/portal/def-work/docs/t2119645.html */
export async function createO2Iteration(options: CreateO2IterationOptions = {}) {
  const repo = options?.repo || getGitRepoInfo().repo;
  const trunk = options?.trunk || getOriginMainBranch();
  const iterationVersion = options?.version || (await getIterationVersion());

  if (options?.branchType === 'new' && !options?.branch) {
    throw new Error('缺少参数 branch');
  }
  /*
   * 新建迭代时是否添加变更的动作:
   * 没有 branchType, 不添加变更
   * branchType = new, 添加新变更, 必传 branch
   * branchType = exist, 添加已有变更，没有时兜底当前分支
   */
  const addBranchParams: Partial<{
    branch_type: 'exist' | 'new';
    branch: string;
    aone_binds: string;
  }> = {};

  if (options?.branchType) {
    // eslint-disable-next-line camelcase
    addBranchParams.branch_type = options.branchType;
    addBranchParams.branch = options.branch || getCurrentBranch();
    // eslint-disable-next-line camelcase
    addBranchParams.aone_binds = options.aoneBinds || '';
  }

  const result = await getO2BucApi<O2IterationSchema>(
    `/v1.0/work/apps/${encodeURIComponent(encodeURIComponent(repo))}/iteration`,
    {
      post: true,
      body: {
        name: options?.name || `迭代 ${iterationVersion}`,
        version: iterationVersion,
        trunk,
        description: options?.description || '',
        ...addBranchParams,
      },
    },
  );
  console.log('创建迭代结果：', result);
  return result;
}

/** 迭代绑定变更, https://icms.alibaba-inc.com/portal/def-work/docs/t2205013.html */
export async function o2IterationBindChange(params: { iterationId: number; branchIds: number[] }) {
  if (!(params?.iterationId && params?.branchIds?.length)) {
    throw new Error(`o2IterationBindChange 参数错误, ${JSON.stringify(params)}`);
  }
  const apiPath = `/v1.0/work/iterations/${params.iterationId}/devbranch/bind`;
  const result = await getO2BucApi(apiPath, {
    post: true,
    body: {
      type: 'exist',
      branchIds: params.branchIds,
      // 以下 type 为 new 时有效，即新建变更并绑定
      // branch,
      // description: '迭代绑定变更',
      // branch_type: 'exist',
      // trunk: '',
      // aone_binds: '',
      // un_release: false,
    },
  });
  logger.note(JSON.stringify(result, null, 2), '迭代绑定变更结果');
  return result;
}
